import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DEFAULT_PRESET, PRESETS, GRANULARITIES, rangeParams, granularityAllowed } from './salesUtils';

const PRESET_KEYS = PRESETS.map((p) => p.key);
const GRAN_KEYS = GRANULARITIES.map((g) => g.key);

const presetFrom = (sp) => {
  const raw = sp.get('range');
  if (raw === 'custom' && sp.get('from') && sp.get('to')) return 'custom';
  return PRESET_KEYS.includes(raw) ? raw : DEFAULT_PRESET;
};

// An explicit granularity is only kept while it is allowed for the resolved range
// (see GRANULARITY_MAX_SPAN_DAYS); otherwise the range falls back to auto.
const granularityFor = (sp, preset) => {
  const raw = sp.get('g');
  if (!GRAN_KEYS.includes(raw)) return 'auto';
  const { from, to } = rangeParams({ preset, from: sp.get('from') || '', to: sp.get('to') || '' });
  return granularityAllowed(raw, from, to) ? raw : 'auto';
};

// Range state lives in the query string (range, from, to, g, cur) so refresh and
// Back restore it, and it carries over between "Analytics by video" and a video panel.
// Range tweaks REPLACE the history entry; opening/closing views pushes (see AdminDashboard).
export default function useSalesRange() {
  const [searchParams, setSearchParams] = useSearchParams();
  const from = searchParams.get('from') || '';
  const to = searchParams.get('to') || '';
  const preset = presetFrom(searchParams);
  const granularity = granularityFor(searchParams, preset);
  const currency = searchParams.get('cur') || '';

  const range = useMemo(
    () => ({ preset, from, to, granularity, currency }),
    [preset, from, to, granularity, currency]
  );
  const params = rangeParams(range);

  const setRange = useCallback((patch) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (patch.preset) {
        next.set('range', patch.preset);
        if (patch.preset === 'custom') {
          next.set('from', patch.from);
          next.set('to', patch.to);
        } else {
          next.delete('from');
          next.delete('to');
        }
      }
      if (patch.granularity) {
        if (patch.granularity === 'auto') next.delete('g'); else next.set('g', patch.granularity);
      }
      if (patch.currency !== undefined) {
        if (patch.currency) next.set('cur', patch.currency); else next.delete('cur');
      }
      if (next.get('g') && granularityFor(next, presetFrom(next)) === 'auto') next.delete('g');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  return { range, params, setRange };
}
